import { createApp } from 'vue'
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import axios from 'axios'
import VueLazyload from 'vue3-lazyload'
import VueAxiosPlugin from './customer/plugin/vue-axios'
import { generateApolloProvider } from './customer/provider/vue-apollo'
import loadingImgUrl from '../images/lazyload/loading.png'
import * as Sentry from '@sentry/browser'
import * as Integrations from '@sentry/integrations'
import { debounce } from 'lodash'

export const setUpVue = () => {
  const vueOptions = {}
  const vueInstances = []

  const requireContext = import.meta.globEager([
    '@/src/js/customer/**/*.vue',
    '@/src/js/customer/**/*.js'
  ])

  Object.keys(requireContext).forEach(key => {
    const name = key.split('/').pop().split('.').shift()
    vueOptions[name] = requireContext[key].default
  })

  if (document.readyState === 'loading') {
    // HTMLパース中に実行可能な要素から順次マウント
    const observer = new MutationObserver(() => {
      mountVueApps(vueOptions, vueInstances)
    })

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    })

    // DOMContentLoadedでオブザーバーを停止して最終マウント
    document.addEventListener('DOMContentLoaded', () => {
      observer.disconnect()
      mountVueApps(vueOptions, vueInstances)
    })
  } else {
    // 既にDOMがロード済みの場合
    mountVueApps(vueOptions, vueInstances)
  }

  window.addEventListener('unload', () => {
    for (const app of vueInstances) {
      app.unmount()
    }
    vueInstances.length = 0
  })
}

// Vueマウント関数
const mountVueApps = (vueOptions, vueInstances) => {
  const apolloProvider = generateApolloProvider()

  const templates = document.querySelectorAll(
    '[data-vue]:not([data-vue-mounted])'
  )

  for (const el of templates) {
    // マウント済みマークを付けて重複マウントを防ぐ
    el.setAttribute('data-vue-mounted', 'true')

    const app = createApp(vueOptions[el.dataset.vue])
    const pinia = createPinia()
    pinia.use(piniaPluginPersistedstate)
    app.use(apolloProvider)
    app.use(pinia)
    app.use(VueLazyload, {
      selector: 'img',
      preLoad: 1.1,
      loading: loadingImgUrl,
      error: 'https://dummyimage.com/500x500/ccc/999.png&text=error'
    })
    app.use(VueAxiosPlugin, { axios })

    app.directive('scroll', {
      beforeMount(el, binding) {
        el.addEventListener('scroll', debounce(binding.value, 100))
      },
      unmounted(el, binding) {
        el.removeEventListener('scroll', debounce(binding.value, 100))
      }
    })
    app.directive('resize', {
      beforeMount(el, binding) {
        // resize の発火頻度を調整している
        // ref: https://jp.vuejs.org/v2/guide/computed.html#%E3%82%A6%E3%82%A9%E3%83%83%E3%83%81%E3%83%A3
        // ref: https://webman-japan.com/vue-event-resize/
        window.addEventListener('resize', debounce(binding.value))
      },
      unmounted(el, binding) {
        window.removeEventListener('resize', debounce(binding.value))
      }
    })

    if (['staging', 'production'].includes(process.env.SENTRY_CURRENT_ENV)) {
      Sentry.init({
        app,
        dsn: 'https://fa22e615e4154f5781a29b6517231694@o100084.ingest.sentry.io/5202269',
        integrations: [
          new Integrations.Dedupe(),
          new Integrations.ExtraErrorData({ depth: 3 })
        ],
        environment: process.env.SENTRY_CURRENT_ENV
      })
    }

    app.mount(el)
    vueInstances.push(app)
  }
}
